/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import React, { useMemo } from 'react'
import { Tooltip, ResponsiveContainer, XAxis, AreaChart, Area, YAxis } from 'recharts'
import { useTranslation } from 'react-i18next'

// Helper function to safely parse monetary values
const parseMonetaryValue = (value) => {
  if (value === null || value === undefined || value === '') return 0
  const parsed = Number(value)
  return isNaN(parsed) ? 0 : Math.max(0, parsed)
}

// Helper function to safely parse and format dates
const parseDate = (dateString) => {
  if (!dateString) return null
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return null
    return date.toISOString().split('T')[0]
  } catch (error) {
    console.warn('Invalid date format:', dateString, error)
    return null
  }
}

// Helper function to check if a date matches the period
const dateMatchesPeriod = (itemDate, targetDate, type) => {
  if (!itemDate || !targetDate) return false
  
  if (type === 'yearly' || type === 'all') {
    // For yearly and 'all' comparison, check if the date starts with YYYY-MM
    return itemDate.startsWith(targetDate)
  } else {
    // For daily/weekly/monthly comparison, exact match
    return itemDate === targetDate
  }
}

const getDates = (type, dateRanges) => {
  // If we have specific date ranges from the main filter, use those
  if (dateRanges && Array.isArray(dateRanges)) {
    return dateRanges.map(date => ({
      date,
      shortenedDate: date.split('-').slice(1).join('-')
    }))
  }

  // Otherwise, generate dates based on type
  const today = new Date()
  
  if (type === 'day') {
    const todayStr = today.toISOString().split('T')[0]
    return [{
      date: todayStr,
      shortenedDate: todayStr.split('-').slice(1).join('-')
    }]
  } else if (type === 'weekly') {
    const startOfWeek = new Date(today)
    startOfWeek.setDate(today.getDate() - today.getDay() + 1)
    const days = []
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek)
      day.setDate(startOfWeek.getDate() + i)
      const dateStr = day.toISOString().split('T')[0]
      days.push({
        date: dateStr,
        shortenedDate: dateStr.split('-').slice(1).join('-')
      })
    }
    return days
  } else if (type === 'monthly') {
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0)
    const days = []
    const currentDate = new Date(startOfMonth)
    while (currentDate <= endOfMonth) {
      const dateStr = currentDate.toISOString().split('T')[0]
      days.push({
        date: dateStr,
        shortenedDate: dateStr.split('-').slice(1).join('-')
      })
      currentDate.setDate(currentDate.getDate() + 1)
    }
    return days
  } else if (type === 'all') {
    // For 'all' type, show all 12 months of the current year
    const currentYear = today.getFullYear()
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                       'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    
    return Array.from({ length: 12 }, (_, i) => {
      const month = String(i + 1).padStart(2, '0')
      const yearMonth = `${currentYear}-${month}`
      return {
        date: yearMonth,
        shortenedDate: monthNames[i]
      }
    })
  }
  
  return []
}

const processData = (expenses, historyOfIncome, dates, type) => {
  return dates.map(({ date, shortenedDate }) => {
    let totalExpenses = 0
    let totalIncome = 0

    // Process expenses with improved error handling and precision
    if (expenses && Array.isArray(expenses)) {
      totalExpenses = expenses
        .filter((item) => {
          if (!item || typeof item !== 'object') return false
          const itemDate = parseDate(item.date)
          return itemDate && dateMatchesPeriod(itemDate, date, type)
        })
        .reduce((acc, curr) => {
          const amount = parseMonetaryValue(curr.paid_amount) || parseMonetaryValue(curr.amount)
          return acc + amount
        }, 0)
    }

    // Process income with improved error handling and precision
    if (historyOfIncome && Array.isArray(historyOfIncome)) {
      totalIncome = historyOfIncome
        .filter((item) => {
          if (!item || typeof item !== 'object') return false
          const itemDate = parseDate(item.date || item.payment_date)
          return itemDate && dateMatchesPeriod(itemDate, date, type)
        })
        .reduce((acc, curr) => {
          const amount = parseMonetaryValue(curr.patient_pay) || 
                        parseMonetaryValue(curr.paid_amount) || 
                        parseMonetaryValue(curr.amount)
          return acc + amount
        }, 0)
    }

    return {
      date,
      shortenedDate,
      Expenses: Math.round(totalExpenses * 100) / 100,
      Income: Math.round(totalIncome * 100) / 100
    }
  })
}

export default function DualLineCharts({ 
  expenses, 
  historyOfIncome, 
  type = 'weekly', 
  showIncome = true, 
  showExpenses = true,
  dateRanges = null // New prop to receive date ranges from main filter
}) {
  const { t } = useTranslation()
  
  // Memoize dates calculation
  const dates = useMemo(() => getDates(type, dateRanges), [type, dateRanges])
  
  // Memoize chart data calculation to improve performance
  const chartData = useMemo(() => {
    return processData(expenses, historyOfIncome, dates, type)
  }, [expenses, historyOfIncome, dates, type])

  // Custom tooltip component with translations and improved formatting
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: '#fff',
          border: '1px solid #ccc',
          padding: '10px',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>
            {t('date')}: {label}
          </p>
          {payload.map((entry, index) => (
            <p key={index} style={{ 
              margin: '2px 0', 
              color: entry.color,
              fontSize: '14px'
            }}>
              {entry.dataKey === 'Expenses' ? t('expenses') : t('income')}: 
              {Number(entry.value).toFixed(2)}€
            </p>
          ))}
          {payload.length === 2 && (
            <p style={{ 
              margin: '5px 0 0 0', 
              fontWeight: 'bold',
              borderTop: '1px solid #eee',
              paddingTop: '5px'
            }}>
              {t('profit')}: {(payload.find(p => p.dataKey === 'Income')?.value - 
                              payload.find(p => p.dataKey === 'Expenses')?.value).toFixed(2)}€
            </p>
          )}
        </div>
      )
    }
    return null
  }

  return (
    <ResponsiveContainer width='100%' height='100%'>
      <AreaChart data={chartData}>
        <YAxis 
          tickFormatter={(value) => `${Number(value).toFixed(0)}€`}
          domain={[0, 'dataMax']}
        />
        <XAxis dataKey='shortenedDate' />
        <Tooltip content={<CustomTooltip />} />
        {showExpenses && (
          <Area 
            type='monotone' 
            dataKey='Expenses' 
            stroke='#ff6b6b' 
            fill='rgba(255, 107, 107, 0.3)' 
            name={t('expenses')}
            strokeWidth={2}
          />
        )}
        {showIncome && (
          <Area 
            type='monotone' 
            dataKey='Income' 
            stroke='#51cf66' 
            fill='rgba(81, 207, 102, 0.3)' 
            name={t('income')}
            strokeWidth={2}
          />
        )}
      </AreaChart>
    </ResponsiveContainer>
  )
}
